import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import PlatformProtectedRoute from "./components/PlatformProtectedRoute";
import RootRedirect from "./components/RootRedirect";
import OfflineBanner from "./components/OfflineBanner";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Employee
import EmployeeLayout from "./pages/employee/EmployeeLayout";
import ScanPage from "./pages/employee/ScanPage";
import InventoryPage from "./pages/employee/InventoryPage";
import RemovalsPage from "./pages/employee/RemovalsPage";
import RequestsPage from "./pages/employee/RequestsPage";
import NotificationsPage from "./pages/NotificationsPage";
import NotificationPreferencesPage from "./pages/NotificationPreferencesPage";

// Admin
import AdminLayout from "./pages/admin/AdminLayout";
import DashboardPage from "./pages/admin/DashboardPage";
import AdminInventoryPage from "./pages/admin/AdminInventoryPage";
import UsersPage from "./pages/admin/UsersPage";
import ProductsPage from "./pages/admin/ProductsPage";
import RequestsAdminPage from "./pages/admin/RequestsAdminPage";
import DepartmentsPage from "./pages/admin/DepartmentsPage";
import HistoryPage from "./pages/admin/HistoryPage";
import AdminNotificationsPage from "./pages/admin/NotificationsPage";
import ReviewQueuePage from "./pages/admin/ReviewQueuePage";
import StoresPage from "./pages/admin/StoresPage";
import CatalogImportPage from "./pages/admin/CatalogImportPage";
import CatalogImportCsvPage from "./pages/admin/CatalogImportCsvPage";
import DepartmentMappingPage from "./pages/admin/DepartmentMappingPage";
import NotificationPolicyPage from "./pages/admin/NotificationPolicyPage";
import AdminRemovalsPage from "./pages/admin/AdminRemovalsPage";
import HQScanPage from "./pages/admin/HQScanPage";

// Platform (Phase 2D — operator-only Control Center inside ShelfLife)
import PlatformLayout from "./pages/platform/PlatformLayout";
import TenantsListPage from "./pages/platform/TenantsListPage";
import CreateTenantForm from "./pages/platform/CreateTenantForm";
import TenantDetailPage from "./pages/platform/TenantDetailPage";
import ActivateAdminFlow from "./pages/platform/ActivateAdminFlow";
// Phase 2E S7 — Catalog Import wizard.
import CatalogImportsListPage from "./pages/platform/CatalogImportsListPage";
import CatalogImportWizardPage from "./pages/platform/CatalogImportWizardPage";
import CatalogImportRunDetailPage from "./pages/platform/CatalogImportRunDetailPage";
// Phase 2F — Super Admin Console drill-downs.
import OperatorStoresPage from "./pages/platform/OperatorStoresPage";
import OperatorStoreDetailPage from "./pages/platform/OperatorStoreDetailPage";
import OperatorUsersPage from "./pages/platform/OperatorUsersPage";
import OperatorDepartmentsPage from "./pages/platform/OperatorDepartmentsPage";
import OperatorDepartmentDetailPage from "./pages/platform/OperatorDepartmentDetailPage";
import OperatorBatchesPage from "./pages/platform/OperatorBatchesPage";
import OperatorRemovalsPage from "./pages/platform/OperatorRemovalsPage";
import OperatorProductRequestsPage from "./pages/platform/OperatorProductRequestsPage";
import {
  getTenantCatalogOnboardingRedirect,
} from "./lib/tenantCatalogAvailability";

function TenantCatalogOnboardingGate({ children }) {
  const redirect = getTenantCatalogOnboardingRedirect();
  if (redirect) {
    return <Navigate to={redirect} replace />;
  }
  return children;
}

function NotificationPreferencesRedirect() {
  const { user } = useAuth();
  if (user?.role === "department_manager") return <main className="p-6"><NotificationPreferencesPage /></main>;
  return <Navigate replace to={user?.role === "admin" || user?.role === "manager" ? "/admin/notification-preferences" : "/notifications/preferences"} />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <OfflineBanner />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/notification-preferences" element={<ProtectedRoute><NotificationPreferencesRedirect /></ProtectedRoute>} />

          <Route
            element={
              <ProtectedRoute roles={["employee", "manager"]}>
                <EmployeeLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/scan" element={<ScanPage />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/removals" element={<RemovalsPage />} />
            <Route path="/requests" element={<RequestsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/notifications/preferences" element={<NotificationPreferencesPage />} />
          </Route>

          <Route
            element={
              <ProtectedRoute roles={["admin", "manager"]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/admin" element={<DashboardPage />} />
            <Route path="/admin/scan" element={<HQScanPage />} />
            <Route path="/admin/inventory" element={<AdminInventoryPage />} />
            <Route path="/admin/removals" element={<AdminRemovalsPage />} />
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/requests" element={<RequestsAdminPage />} />
            <Route path="/admin/history" element={<HistoryPage />} />
            <Route path="/admin/notifications" element={<AdminNotificationsPage />} />
            <Route path="/admin/notification-preferences" element={<NotificationPreferencesPage />} />
          </Route>

          <Route
            element={
              <ProtectedRoute role="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/admin/stores" element={<StoresPage />} />
            <Route path="/admin/products" element={<ProductsPage />} />
            <Route
              path="/admin/catalog-import"
              element={<TenantCatalogOnboardingGate><CatalogImportPage /></TenantCatalogOnboardingGate>}
            />
            <Route
              path="/admin/catalog-import-csv"
              element={<TenantCatalogOnboardingGate><CatalogImportCsvPage /></TenantCatalogOnboardingGate>}
            />
            <Route path="/admin/departments" element={<DepartmentsPage />} />
            <Route
              path="/admin/department-mapping"
              element={<TenantCatalogOnboardingGate><DepartmentMappingPage /></TenantCatalogOnboardingGate>}
            />
            <Route path="/admin/notification-policy" element={<NotificationPolicyPage />} />
            <Route path="/admin/review-queue" element={<ReviewQueuePage />} />
          </Route>

          {/* Platform operator-only surface (Phase 2D).
              Strict role gate — customers redirected silently. */}
          <Route
            element={
              <PlatformProtectedRoute>
                <PlatformLayout />
              </PlatformProtectedRoute>
            }
          >
            <Route path="/platform" element={<TenantsListPage />} />
            <Route path="/platform/companies/new" element={<CreateTenantForm />} />
            <Route path="/platform/companies/:cid" element={<TenantDetailPage />} />
            <Route path="/platform/companies/:cid/activate-admin" element={<ActivateAdminFlow />} />
            {/* Phase 2E S7 — Operator Catalog Import wizard. */}
            <Route
              path="/platform/companies/:cid/catalog/imports"
              element={<CatalogImportsListPage />}
            />
            <Route
              path="/platform/companies/:cid/catalog/imports/new"
              element={<CatalogImportWizardPage />}
            />
            <Route
              path="/platform/companies/:cid/catalog/imports/:runId"
              element={<CatalogImportRunDetailPage />}
            />
            {/* Phase 2F — Super Admin Console drill-downs. */}
            <Route
              path="/platform/companies/:cid/stores"
              element={<OperatorStoresPage />}
            />
            <Route
              path="/platform/companies/:cid/stores/:storeId"
              element={<OperatorStoreDetailPage />}
            />
            <Route
              path="/platform/companies/:cid/users"
              element={<OperatorUsersPage />}
            />
            <Route
              path="/platform/companies/:cid/departments"
              element={<OperatorDepartmentsPage />}
            />
            <Route
              path="/platform/companies/:cid/departments/:deptId"
              element={<OperatorDepartmentDetailPage />}
            />
            <Route
              path="/platform/companies/:cid/batches"
              element={<OperatorBatchesPage />}
            />
            <Route
              path="/platform/companies/:cid/removals"
              element={<OperatorRemovalsPage />}
            />
            <Route
              path="/platform/companies/:cid/product-requests"
              element={<OperatorProductRequestsPage />}
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
